'use client';

import type {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
} from 'react';

import { forwardRef, useContext, createContext } from 'react';

import type { DateRange } from 'react-aria-components';
import {
  Button,
  DatePicker,
  DatePickerStateContext,
  DateRangePickerStateContext,
  DateRangePicker,
  Group,
  I18nProvider,
  Label,
} from 'react-aria-components';

import { Calendar, RangeCalendar } from './calender';
import { DateInput } from './datepicker';
import { cn } from './helpers';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type TiclickDatePickerMode = 'single' | 'range';

const TiclickDatePickerContext = createContext<{
  mode: TiclickDatePickerMode;
} | null>(null);

const useTiclickDatePickerMode = () => {
  const ctx = useContext(TiclickDatePickerContext);
  if (!ctx) {
    throw new Error(
      'TiclickDatePicker components must be used inside <TiclickDatePicker />'
    );
  }
  return ctx;
};

type TDateRangePickerProps = {
  mode: 'range';
} & ComponentProps<typeof DateRangePicker>;

type TDateSinglePickerProps = {
  mode?: 'single';
} & ComponentProps<typeof DatePicker>;

// Implementation
function TiclickDatePicker({
  className,
  locale = 'en-GB',
  mode = 'single',
  ...props
}: (TDateSinglePickerProps | TDateRangePickerProps) &
  ComponentProps<typeof I18nProvider>) {
  if (mode === 'single') {
    const typedProps = props as TDateSinglePickerProps;
    return (
      <Popover>
        <TiclickDatePickerContext.Provider value={{ mode }}>
          <I18nProvider locale={locale}>
            <DatePicker
              className={cn('group *:not-first:mt-2', className)}
              {...typedProps}
            />
          </I18nProvider>
        </TiclickDatePickerContext.Provider>
      </Popover>
    );
  }

  const typedProps = props as TDateRangePickerProps;
  return (
    <Popover>
      <TiclickDatePickerContext.Provider value={{ mode }}>
        <I18nProvider locale={locale}>
          <DateRangePicker
            className={cn('group *:not-first:mt-2', className)}
            {...typedProps}
          />
        </I18nProvider>
      </TiclickDatePickerContext.Provider>
    </Popover>
  );
}

const TiclickDatePickerInputGroup = ({
  className,
  ...props
}: ComponentProps<typeof Group>) => {
  return (
    <Group
      className={cn('relative w-full', className)}
      {...props}
    />
  );
};

const TiclickDatePickerLabel = ({
  className,
  ...props
}: ComponentProps<typeof Label>) => {
  return (
    <Label
      className={cn(
        'text-sm font-medium text-gray-800 leading-none',
        className
      )}
      {...props}
    />
  );
};

const TiclickDatePickerInput = ({
  className,
  ...props
}: ComponentProps<typeof DateInput>) => {
  return (
    <DateInput
      className={cn('pe-2', className)}
      {...props}
    />
  );
};

const TiclickDatePickerGroupAddon = ({
  className,
  align = 'end',
  ...props
}: ComponentProps<'div'> & { align?: 'start' | 'end' }) => {
  return (
    <div
      slot='end'
      className={cn(
        'absolute -translate-y-1/2 h-[-webkit-fill-available] top-1/2',
        align === 'start' && 'start-2',
        align === 'end' && 'end-2',
        className
      )}
      {...props}
    />
  );
};

const TiclickDatePickerClearValue = ({
  className,
  value,
  onPress,
  ...props
}: ComponentProps<typeof Button> & { value?: 'start' | 'end' }) => {
  const singleDateState = useContext(DatePickerStateContext);
  const rangeDateState = useContext(DateRangePickerStateContext);
  const { mode } = useTiclickDatePickerMode();

  // Only render if there's a value
  if (mode === 'single') {
    if (!singleDateState?.value) return null;
  } else {
    if (!rangeDateState?.value) return null;
    // If specific value (start/end) is provided, check if that specific value exists
    if (value && !rangeDateState?.value?.[value]) return null;
  }

  return (
    <Button
      slot={null}
      className={cn(
        'cursor-pointer flex items-center justify-center group-has-disabled:hidden',
        className
      )}
      aria-label='Clear'
      {...props}
      onPress={(e) => {
        if (mode === 'single') {
          singleDateState?.setValue(null);
        } else {
          if (!value) {
            rangeDateState?.setValue(null);
          } else {
            const shallowCopyValue = { ...(rangeDateState || {}).value };

            if (value === 'start') {
              delete shallowCopyValue.start;
            } else {
              delete shallowCopyValue.end;
            }

            rangeDateState?.setValue(shallowCopyValue as DateRange);
          }
        }
        onPress?.(e);
      }}
    />
  );
};

const TiclickDatePickerCalendarPopoverTrigger = forwardRef<
  ElementRef<typeof Button>,
  ComponentPropsWithoutRef<typeof PopoverTrigger>
>(({ className, ...props }, ref) => {
  return (
    <PopoverTrigger
      ref={ref}
      className={cn('w-max cursor-pointer', className)}
      {...props}
    />
  );
});

TiclickDatePickerCalendarPopoverTrigger.displayName =
  'TiclickDatePickerCalendarPopoverTrigger';

function isNestedDropdownTarget(target: EventTarget | null) {
  const element =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null;

  return Boolean(
    element?.closest(
      '[data-slot="dropdown-menu-content"], [data-radix-menu-content], [role="menu"]'
    )
  );
}

const TiclickDatePickerCalendarPopover = ({
  children,
  closeOnSelect,
  onInteractOutside,
  onFocusOutside,
  ...props
}: ComponentProps<typeof PopoverContent> & { closeOnSelect?: boolean }) => {
  const { mode } = useTiclickDatePickerMode();

  return (
    <PopoverContent
      {...props}
      onInteractOutside={(event) => {
        if (isNestedDropdownTarget(event.target)) {
          event.preventDefault();
        }
        onInteractOutside?.(event);
      }}
      onFocusOutside={(event) => {
        if (isNestedDropdownTarget(event.target)) {
          event.preventDefault();
        }
        onFocusOutside?.(event);
      }}
    >
      {mode === 'range' ? (
        <RangeCalendar closeOnSelect={closeOnSelect} />
      ) : (
        <Calendar closeOnSelect={closeOnSelect} />
      )}
      {children}
    </PopoverContent>
  );
};

const TiclickDatePickerCalendarPopoverFooter = ({
  className,
  ...props
}: ComponentProps<'div'>) => {
  return (
    <div
      className={cn('mt-2', className)}
      {...props}
    />
  );
};

export {
  TiclickDatePicker,
  TiclickDatePickerInputGroup,
  TiclickDatePickerLabel,
  TiclickDatePickerInput,
  TiclickDatePickerGroupAddon,
  TiclickDatePickerClearValue,
  TiclickDatePickerCalendarPopover,
  TiclickDatePickerCalendarPopoverFooter,
  TiclickDatePickerCalendarPopoverTrigger,
};
